import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  collection,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { MdBusiness, MdFitnessCenter, MdWarning } from 'react-icons/md';
import Card from 'components/card/Card';
import { AuthContext } from 'contexts/AuthContext';
import { app } from 'lib/firebase';
import ClassDetailsModal from 'views/admin/calendar/components/InstructorClassDetailsModal';
import {
  buildAssignedInstructorClassesQuery,
  filterNeedsAttentionClasses,
  sortAssignedClassesByDate,
} from 'utils/instructorClassQueries';
import { getClassDisplayText, getClassDurationMinutes, formatClassDateTimeLA } from 'utils/instructorClassWorkflows';
import { getClassTypeIcon } from 'utils/classIcons';

const db = getFirestore(app);

export default function InstructorNeedsAttentionClasses(props) {
  const { ...rest } = props;
  const { userDocId } = useContext(AuthContext);
  const [classes, setClasses] = useState([]);
  const [facilitiesById, setFacilitiesById] = useState({});
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTourPreview, setShowTourPreview] = useState(false);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('gray.700', 'gray.400');
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('red.200', 'red.700');
  const warningBg = useColorModeValue('red.50', 'whiteAlpha.100');
  const iconFilter = 'brightness(0) invert(1)';

  useEffect(() => {
    if (!userDocId) {
      setClasses([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const assignedQuery = buildAssignedInstructorClassesQuery({
      db,
      collection,
      query,
      where,
      instructorId: userDocId,
    });

    const unsubscribe = onSnapshot(
      assignedQuery,
      (snapshot) => {
        const nextClasses = snapshot.docs.map((classDoc) => ({
          id: classDoc.id,
          ...classDoc.data(),
        }));
        setClasses(sortAssignedClassesByDate(filterNeedsAttentionClasses(nextClasses)));
        setLoading(false);
      },
      (error) => {
        console.error('Error loading instructor needs attention classes:', error);
        setLoading(false);
      }
    );

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [userDocId]);

  useEffect(() => {
    const handleTourStepChange = (event) => {
      setShowTourPreview(event.detail?.targetId === 'needs-attention-widget');
    };

    window.addEventListener('instructorPortalTourStepChange', handleTourStepChange);
    return () => {
      window.removeEventListener('instructorPortalTourStepChange', handleTourStepChange);
    };
  }, []);

  const visibleClasses = useMemo(() => classes.slice(0, 4), [classes]);

  useEffect(() => {
    let isActive = true;
    const missingFacilityIds = Array.from(new Set(
      visibleClasses
        .map((classData) => classData.FacilityID)
        .filter((facilityId) => facilityId && !facilitiesById[facilityId])
    ));

    if (missingFacilityIds.length === 0) return undefined;

    const loadFacilities = async () => {
      const entries = await Promise.all(missingFacilityIds.map(async (facilityId) => {
        const facilityDoc = await getDoc(doc(db, 'Users', facilityId));
        return [facilityId, facilityDoc?.exists?.() ? facilityDoc.data() : null];
      }));

      if (!isActive) return;
      setFacilitiesById((current) => ({
        ...current,
        ...Object.fromEntries(entries),
      }));
    };

    loadFacilities();

    return () => {
      isActive = false;
    };
  }, [visibleClasses, facilitiesById]);

  if (!loading && visibleClasses.length === 0 && !showTourPreview) return null;

  return (
    <Card p="20px" align="stretch" direction="column" w="100%" border="1px solid" borderColor={borderColor} {...rest}>
      <Flex alignItems="center" w="100%" mb="18px" justify="space-between">
        <Flex alignItems="center" gap="10px" minW="0" flex="1">
          <Flex w="36px" h="36px" borderRadius="12px" bg="red.500" align="center" justify="center">
            <Icon as={MdWarning} color="white" w="22px" h="22px" />
          </Flex>
          <Box minW="0" textAlign="left">
            <Text color={textColor} fontSize="xl" fontWeight="800" lineHeight="1.1">
              Needs Attention
            </Text>
            <Text color="red.500" fontSize="sm" fontWeight="800" mt="4px">
              You will not be paid for your classes until you complete your class completion form by clicking the "Get Paid" button for each class.
            </Text>
          </Box>
        </Flex>
        {classes.length > visibleClasses.length && (
          <Badge colorScheme="red" borderRadius="8px" px="10px">
            {classes.length} pending
          </Badge>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" w="100%" py="30px">
          <Spinner color="red.500" size="lg" thickness="4px" />
        </Flex>
      ) : (
        <Flex direction="column" w="100%" gap="12px">
          {showTourPreview && visibleClasses.length === 0 ? (
            <Flex
              data-testid="needs-attention-tour-preview"
              w="100%"
              p="16px"
              bg={warningBg}
              borderRadius="12px"
              border="1px dashed"
              borderColor={borderColor}
              align="center"
              justify="space-between"
              gap="12px"
              wrap="wrap"
            >
              <Flex align="center" flex="1" minW="220px">
                <Flex bg="red.500" borderRadius="10px" w="42px" h="42px" align="center" justify="center" me="12px" flexShrink="0">
                  <Icon as={MdFitnessCenter} color="white" w="22px" h="22px" />
                </Flex>
                <Box minW="0">
                  <Text color={textColor} fontSize="md" fontWeight="800" lineHeight="1.2">
                    Example completion form
                  </Text>
                  <Text color={textColorSecondary} fontSize="xs" fontWeight="600" mt="3px">
                    Past classes needing a class completion form will show here.
                  </Text>
                </Box>
              </Flex>

              <Flex align="center" gap="8px" bg={cardBg} px="10px" py="8px" borderRadius="10px" minW="180px">
                <Icon as={MdBusiness} color="brand.500" w="18px" h="18px" />
                <Box minW="0">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    Example facility
                  </Text>
                  <Text color={textColorSecondary} fontSize="xs">
                    Tour preview only
                  </Text>
                </Box>
              </Flex>

              <Button colorScheme="red" color="white" flexShrink="0" isDisabled>
                Get Paid
              </Button>
            </Flex>
          ) : visibleClasses.map((classItem) => {
            const facility = facilitiesById[classItem.FacilityID] || {};
            const facilityName = getClassDisplayText(
              facility.FacilityName || facility.FirstName || classItem.FacilityName,
              'Facility'
            );
            const className = getClassDisplayText(
              classItem.ClassName || classItem.ClassType,
              'Unnamed Class'
            );
            const classIcon = getClassTypeIcon(classItem.ClassName || classItem.ClassType);

            return (
              <Flex
                key={classItem.id}
                data-testid={`needs-attention-class-card-${classItem.id}`}
                w="100%"
                p="16px"
                bg={cardBg}
                borderRadius="12px"
                border="1px solid"
                borderColor={borderColor}
                align="center"
                justify="space-between"
                gap="12px"
                wrap="wrap"
                cursor="pointer"
                onClick={() => setSelectedClass(classItem)}
                _hover={{ borderColor: 'red.500', boxShadow: 'sm' }}
              >
                <Flex align="center" flex="1" minW="220px">
                  <Flex bg="red.500" borderRadius="10px" w="42px" h="42px" align="center" justify="center" me="12px" flexShrink="0">
                    {classIcon ? (
                      <Image
                        src={classIcon}
                        alt={className}
                        w="26px"
                        h="26px"
                        objectFit="contain"
                        filter={iconFilter}
                      />
                    ) : (
                      <Icon as={MdFitnessCenter} color="white" w="22px" h="22px" />
                    )}
                  </Flex>
                  <Box minW="0">
                    <Text color={textColor} fontSize="md" fontWeight="800" lineHeight="1.2" noOfLines={1}>
                      {className}
                    </Text>
                    <Text color={textColorSecondary} fontSize="xs" fontWeight="600" mt="3px">
                      {formatClassDateTimeLA(classItem.Date)}
                    </Text>
                  </Box>
                </Flex>

                <Flex align="center" gap="8px" bg={warningBg} px="10px" py="8px" borderRadius="10px" minW="180px">
                  <Icon as={MdBusiness} color="brand.500" w="18px" h="18px" />
                  <Box minW="0">
                    <Text color={textColor} fontSize="sm" fontWeight="700" noOfLines={1}>
                      {facilityName}
                    </Text>
                    <Text color={textColorSecondary} fontSize="xs">
                      {getClassDurationMinutes(classItem)} minutes
                    </Text>
                  </Box>
                </Flex>

                <Button
                  colorScheme="red"
                  color="white"
                  flexShrink="0"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedClass(classItem);
                  }}
                >
                  Get Paid
                </Button>
              </Flex>
            );
          })}
        </Flex>
      )}

      <ClassDetailsModal
        isOpen={Boolean(selectedClass)}
        onClose={() => setSelectedClass(null)}
        classData={selectedClass}
        facility={selectedClass ? facilitiesById[selectedClass.FacilityID] : null}
      />
    </Card>
  );
}
